import { ePartyPurpose } from '../../enums/ePartyPurpose';
import { ePartyType } from '../../enums/ePartyType';
import { v4 as uuidv4 } from 'uuid';
import { IPropertyAddress } from './IPropertyAddress';
import { ePartyTypeData } from '../../enums/ePartyTypeData';
import { INotaryDetailsData } from '../../../types/signingAppointment/INotaryDetailsData';
import { hasEmail, IHasEmail } from '../../../types/shared/IHasEmail';

export interface IPartyPerson {
  id: string;
  type: ePartyType.person;
  purpose: ePartyPurpose;
  email?: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  phone_number?: string;
  marital_status?: string;
  ssn?: string;
  registered?: boolean;
  party_sequence?: number;
  lender_user_id?: string;
  read_only?: boolean;
  address?: IPropertyAddress;
  date_of_birth?: string;
  zuid?: string;
}

export type ICreatePartyPerson = Pick<IPartyPerson, 'purpose' | 'first_name' | 'last_name'> & Partial<IPartyPerson>;

export const createPartyPerson = ({
  id,
  purpose,
  email,
  first_name,
  last_name,
  marital_status,
  middle_name,
  phone_number,
  ssn,
  registered,
  party_sequence,
  lender_user_id,
  read_only,
  address,
  date_of_birth,
  zuid,
}: ICreatePartyPerson): IPartyPerson => ({
  purpose,
  first_name,
  last_name,
  party_sequence,
  lender_user_id,
  read_only,
  id: id ?? uuidv4(),
  type: ePartyType.person,
  email: email,
  marital_status: marital_status,
  middle_name: middle_name,
  phone_number: phone_number,
  ssn: ssn,
  registered: registered,
  address: address,
  date_of_birth: date_of_birth,
  zuid: zuid,
});

export interface IPartyPersonData {
  id: string;
  partyType: ePartyTypeData.Person;
  purpose: ePartyPurpose;
  email?: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  phoneNumber?: string;
  maritalStatus?: string;
  ssn?: string;
  registered?: boolean;
  partySequence?: number;
  lenderUserId?: string;
  readOnly?: boolean;
  createdAt: string;
  invitationCode?: string;
  dateOfBirth?: string; // ISO String
  address?: IPropertyAddress;
  zuid?: string;
}

export type ICreatePartyPersonData = Pick<IPartyPersonData, 'purpose' | 'firstName' | 'lastName' | 'createdAt'> &
  Partial<IPartyPersonData>;

export const createPartyPersonData = ({
  id,
  purpose,
  email,
  firstName,
  lastName,
  maritalStatus,
  middleName,
  phoneNumber,
  ssn,
  registered,
  partySequence,
  lenderUserId,
  readOnly,
  createdAt,
  dateOfBirth,
  address,
}: ICreatePartyPersonData): IPartyPersonData => ({
  purpose,
  firstName,
  lastName,
  partySequence,
  lenderUserId,
  readOnly,
  createdAt,
  id: id !== undefined ? id : uuidv4(),
  partyType: ePartyTypeData.Person,
  email: email,
  maritalStatus: maritalStatus,
  middleName: middleName,
  phoneNumber: phoneNumber,
  ssn: ssn,
  registered: registered,
  dateOfBirth: dateOfBirth,
  address: address,
});

export const createNotaryData = (notary: INotaryDetailsData | undefined): IPartyPerson => {
  return notary
    ? {
        id: notary.id,
        first_name: notary.firstName ? notary.firstName : '',
        last_name: notary.lastName ? notary.lastName : '',
        email: notary.email,
        phone_number: notary.phoneNumber,
        purpose: ePartyPurpose.notary,
        type: ePartyType.person,
      }
    : {
        id: '',
        first_name: '',
        last_name: '',
        phone_number: '',
        email: '',
        type: ePartyType.person,
        purpose: ePartyPurpose.notary,
      };
};

export interface IPartyPersonWithEmail extends Omit<IPartyPerson, 'email'>, IHasEmail {}
export interface IPartyPersonDataWithEmail extends Omit<IPartyPersonData, 'email'>, IHasEmail {}

export const isPartyPersonWithEmail = (person: IPartyPerson): person is IPartyPersonWithEmail => {
  return hasEmail(person);
};

export const isPartyPersonDataWithEmail = (person: IPartyPersonData): person is IPartyPersonDataWithEmail => {
  return hasEmail(person);
};
